import { useCallback, useEffect, useState } from 'react'
import { useParams } from "react-router-dom"
import { faCheckCircle, faDownload, faInbox } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import numeral from 'numeral'
import dayjs from 'dayjs'

import { message, Result, Button } from 'antd'

import ButtonPrimary from "components/ButtonPrimary"
import Loading from 'components/Loading'
import ViewPDF from "components/ViewPDF"
import TransferUploadPanel from "components/TransferUploadPanel"

import { discectError, getSuretyLogo } from 'lib/utils'
import api from 'lib/api'

import styles from './styles.module.scss'

const StatusDownload = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [transfer, setTransfer] = useState(null)
  const [fileUploads, setFileUploads] = useState({})
  const [viewPdf, setViewPdf] = useState(null)
  const [isMobile, setIsMobile] = useState(false)

  const { transferId } = useParams()

  const [messageApi, contextHolder] = message.useMessage()

  const _setTransfers = useCallback((incomingTransfer) => {
    let newFileUploads = {}
    incomingTransfer.metadata.powers.forEach(power => {
      let powerId = power.powerId || null
      if (powerId && power.powerProof) {
        newFileUploads[powerId] = power.powerProof
      }
    })

    setTransfer(incomingTransfer)
    setFileUploads(v => newFileUploads)
  }, [setTransfer])

  const _fetchTransfer = useCallback(async () => {
    setIsLoading(true)
    try {
      const { transfer: incomingTransfer } = await api.inventory.getTransfer(transferId)
      _setTransfers(incomingTransfer)
    } catch(e) {
      console.error(e)
      messageApi.open({
        type: 'error',
        content: discectError(e)
      })
    }
    setIsLoading(false)
  }, [transferId, messageApi, _setTransfers])

  useEffect(() => {
    if (!transfer) _fetchTransfer()
  }, [_fetchTransfer, transferId, transfer, messageApi])

  useEffect(() => {
    window.mobileCheck() ? setIsMobile(true) : setIsMobile(false)
  }, [])

  // Actions

  const _updateTransfer = useCallback(async (powerId, data) => {
    setIsLoading(true)
    try {
      await api.inventory.updateTransferPowerProof(transferId, powerId, data)
      _fetchTransfer()
    } catch(e) {
      console.error(e)
      messageApi.open({
        type: 'error',
        content: discectError(e)
      })
    }
    setIsLoading(false)
  }, [transferId, messageApi, _fetchTransfer])

  const _getFeeTotalByType = (type) => {
    let total = 0
    transfer?.metadata?.fees?.forEach(itm => {
      if (itm.type === type) total += parseFloat(itm.amount)
    })
    return numeral(total).format('0,0.00')
  }

  const _generateReceipt = async () => {
    setIsLoading(true)
    try {
      let postingAgency = []
      let postingAgent = []
      transfer?.metadata?.powers?.forEach(itm => {
        postingAgency.push(itm.postingAgency)
        postingAgent.push(itm.postingAgent)
      })

      let total = 0
      transfer?.metadata?.fees?.forEach(itm => {
        total += parseFloat(itm.amount)
      })

      const params = {
        document: `RECEIPT`,
        documentParams: {
          date: dayjs(transfer.date).format('DD-MMM-YY'),
          defendantLastName: transfer?.metadata?.defendant?.nameLast || '',
          defendantFirstName: transfer?.metadata?.defendant?.nameFirst || '',
          defendantMiddleName: transfer?.metadata?.defendant?.nameMiddle || '',
          powerNumbers: transfer?.metadata?.powers.map(power => (power.powerNumber || 'N/A')).join(', ') || '',
          postingDate: transfer?.postingDate ? dayjs(transfer.postingDate).format('DD-MMM-YY') : 'N/A',
          bondTotals: transfer?.metadata?.powers.map(power => (power.bondAmount ? numeral(power.bondAmount).format('0,0.00') : 'N/A')).join(', ') || '',
          state: transfer?.metadata?.state || '',
          county: transfer?.metadata?.county || '',
          postingAgency: [...new Set(postingAgency)].join(', ') || '',
          postingAgent: [...new Set(postingAgent)].join(', ') || '',
          postingFees: _getFeeTotalByType('POSTING_AGENT_FEE'),
          jailFees: _getFeeTotalByType('JAIL_FEE'),
          stateFees: _getFeeTotalByType('STATE_FEE'),
          otherFees: _getFeeTotalByType('OTHER'),
          totalAmount: numeral(total).format('0,0.00')
        }
      }

      const { url } = await api.inventory.generateTransferDocument(transfer.id, params)
      if (isMobile) {
        setViewPdf(url)
      } else {
        window.open(url, '_blank')
      }
    } catch(e) {
      console.error(e)
      messageApi.open({
        type: 'error',
        content: discectError(e)
      })
    }
    setIsLoading(false)
  }

  const _getViewPdf = () => {
    if (viewPdf) {
      return (
        <ViewPDF 
          isWatermarkDisabled
          url={viewPdf}
          text="Print the Power by clicking the Print Button"
          onClose={() => setViewPdf(null)}
        />
      )
    }
    return null
  }

  return (
    <>
      {contextHolder}
      <div className={styles.transfer__container}>
        <div className={styles.transfer}>
          {error && (
            <Result
              status="error"
              title="Submission Failed"
              subTitle={error}
              extra={[
                <Button onClick={() => setError(null)}>
                  Go Back
                </Button>
              ]}
            ></Result>
          )}
          {(!error && transfer) && (
            <>
              <header>
                {getSuretyLogo(transfer?.suretyId, transfer?.suretyName)}
                <p>Use the area below to upload power proofs of the powers you used for this transfer.  Once all have been uploaded, you will be able to download your receipt, and we will move this finance for payment.</p>
              </header>
              <div className={styles.receiptArea}>
                <div>
                  <h4>{Object.keys(fileUploads).length} of {transfer?.metadata?.powers.length} Power Proofs Uploaded</h4>
                  <p>Once you have uploaded Power Proofs of all of the Powers you will be able to download your Receipt.</p>
                </div>
                <ButtonPrimary
                  fullWidth
                  icon={faDownload}
                  color="teal"
                  onClick={() => _generateReceipt()}
                >
                  {isMobile ? 'View Receipt' : 'Download Receipt'}
                </ButtonPrimary>
              </div>
              <div className={styles.uploadPanels__container}>
                {transfer?.metadata?.powers.map((power, index) => {
                  let powerId = power.powerId || null
                  return (
                    <TransferUploadPanel
                      key={index}
                      id={`powerProof-${power.id}`}
                      transferId={transfer.id}
                      storageKey={`surety/${transfer.suretyId}/power/${powerId}`}
                      meta={{
                        name: 'Power Proof',
                        description: `Power Proof for Power #${power.powerNumber}`,
                        type: 'POWER_PROOF'
                      }}
                      onRefresh={data => {
                        _updateTransfer(powerId, data)
                        setFileUploads(v => ({ ...v, [powerId]: data }))
                        // onRefresh()
                      }}
                    >
                      {(powerId && fileUploads[powerId]) && (
                        <div className={styles.uploadPanels__completed}>
                          <p className={styles.uploadPanels__completed__icon}>
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </p>
                          <div className={styles.uploadPanels__details}>
                            <h3>#{power.powerNumber || 'N/A'}</h3>
                            <p>Bond Amount: <strong>{numeral(power.bondAmount).format('0,0')}</strong></p>
                            <p>Charge: <strong>{power.charge}</strong></p>
                          </div>
                          <p className={styles.uploadPanels__completed__text}>Your file has been uploaded!</p>
                        </div>
                      )}
                      {(!powerId || !fileUploads[powerId]) && (
                        <div className={styles.uploadPanels__pending}>
                          <p className={styles.uploadPanels__pending__icon}>
                            <FontAwesomeIcon icon={faInbox} />
                          </p>
                          <div className={styles.uploadPanels__details}>
                            <h3>#{power.powerNumber || 'N/A'}</h3>
                            <p>Bond Amount: <strong>{numeral(power.bondAmount).format('0,0')}</strong></p>
                            <p>Charge: <strong>{power.charge}</strong></p>
                          </div>
                          <p className={styles.uploadPanels__pending__text}>Click or drag file to this area to upload</p>
                        </div>
                      )}
                    </TransferUploadPanel>
                  )
                })}
              </div>
            </>
          )}
        </div>
        <div className={styles.poweredBy}>
          <h5 className="rad">Powered by</h5>
          <a href="https://simplybail.com" target="_blank" rel="noreferrer">
            <img src="/images/SimplyBail_logo_long.png" alt="SimplyBail" />
          </a>
        </div>
      </div>
      {isLoading && <Loading />}
      {_getViewPdf()}
    </>
  ) 
}

export default StatusDownload